import { CardTransactionType, PaymentDTO } from 'core/api';
import { paymentRequiresCardProcessor, paymentRequiresCashControl } from './ExternalPaymentsService.Common';


type RefundStatus = 'pending' | 'success' | 'error' | 'ignored-error';
export type RefundPaymentStatus = { payment: PaymentDTO, status: RefundStatus }

type OnStatusesChangedCallback = () => void;

export class RefundPaymentsProcessor {

	private statuses: RefundPaymentStatus[];

	constructor(
		payments: PaymentDTO[],
		private readonly revertPayment: (payment: PaymentDTO) => Promise<{ ok: boolean, refundPayment: PaymentDTO }>,
		private readonly discardPayment: (payment: PaymentDTO) => Promise<void>,
		private readonly onPaymentsStatusChanged: OnStatusesChangedCallback,
	) {
		this.statuses = this.buildInitialStatuses(payments);
	}

	private buildInitialStatuses(payments: PaymentDTO[]): RefundPaymentStatus[] {

		// Agrupo los pagos en efectivo para devolverlos de una vez. La propina no la contamos va al bote
		const cashPayments = payments.filter(x => x.IsCash);
		const finalCashPayment = cashPayments.groupBy(x => x.IsCash).map(group => ({ ...group.value[0], Amount: group.value.sum(x => x.Amount), TipAmount: 0 }));
		const cardPayments = payments.filter(x => (paymentRequiresCardProcessor(x) && x.CardTransaction.TransactionId.length > 0));
		// Ordeno los pagos, primero los que actuan con pasarelas o máquinas de efectivo
		const paymentsToRefund: PaymentDTO[] = [...cardPayments, ...finalCashPayment, ...payments.except(cashPayments).except(cardPayments)];

		return paymentsToRefund.map(payment => ({ status: 'pending', payment }));
	}

	public getStatuses(): RefundPaymentStatus[] {
		return [...this.statuses];
	}

	public getPayments(): PaymentDTO[] {
		return this.statuses.map(x => x.payment);
	}

	public hasFinished(): boolean {
		return this.statuses.every(x => x.status === 'success' || x.status === 'ignored-error');
	}

	public canIgnoreLastPayment(): boolean {
		// Puedo ignorar un pago, cuando al menos ya se ha completado un pago externo previamente de alguna manera, ya sea con error o no. Si no estoy al 
		// inicio del proceso y podría evitar continuar
		return this.statuses
			.filter(x => (paymentRequiresCardProcessor(x.payment) && x.payment.CardTransaction.TransactionId.length > 0) || paymentRequiresCashControl(x.payment))
			.filter(x => x.status !== 'pending').length > 1;
	}

	public getLastPaymentWithError(): Nullable<PaymentDTO> {
		return this.statuses.filter(x => x.status === 'error').last()?.payment ?? null;
	}

	public hasBeenStarted(): boolean {
		return this.statuses.some(x => x.status !== 'pending');
	}

	private updateStatus(index: number, payment: PaymentDTO, status: RefundStatus) {
		this.statuses[index] = { payment, status };
		this.onPaymentsStatusChanged();
	}

	public async retryLastPayment(): Promise<void> {

		const payment = this.getLastPaymentWithError();
		if (payment === null)
			throw new Error('No había ningún pago con error para reintentar');

		const { ok, refundPayment } = await this.revertPayment(payment);
		if (ok) {
			const paymentIndex = this.statuses.findIndex(x => x.payment === payment);
			this.updateStatus(paymentIndex, refundPayment, 'success');
			await this.start();
		}
	}

	public async ignoreLastPayment(): Promise<void> {

		const payment = this.getLastPaymentWithError();
		if (payment === null)
			throw new Error('No había ningún pago con error para ignorar');

		await this.discardPayment(payment);

		const paymentIndex = this.statuses.findIndex(x => x.payment === payment);
		this.updateStatus(paymentIndex, { ...payment, CardTransaction: { TransactionId: '', TransactionType: CardTransactionType.Adyen, XmlDetails: '' } }, 'ignored-error');
		await this.start();
	}

	public async start(): Promise<void> {

		const pendingEntries = this.statuses
			.map((x, i) => ({ ...x, index: i }))
			.filter(x => x.status === 'pending' || x.status === 'error');

		for (const { payment, index } of pendingEntries) {
			const { ok, refundPayment } = await this.revertPayment(payment);
			this.updateStatus(index, refundPayment, ok ? 'success' : 'error');
			if (!ok)
				break;
		}
	}
}